import { defaultVK } from '@hid/define/define_keys'
import { type KK, OutputMode, type Output, KIO, type VK, HuoChaiRenDevices } from '../../define/define'
import { SetKeyType, type PostSetKey } from '../../define/request'
import { deviceManager } from '../../device/device_manager'
import { dataProfileManage, type DataProfile } from '../../snapshot/profile'
import { markKmeKeys } from './handle_set_magnet_senior_key'
import { MagnetSeniorType } from '@libs/types/enum'
import { InternalActionKeyId } from '@/define/internal_action_keys'

const onboardSwitchActionIds = new Set<VK>([InternalActionKeyId.deviceSwitchToProfile1 as VK, InternalActionKeyId.deviceSwitchToProfile2 as VK, InternalActionKeyId.deviceSwitchToProfile3 as VK, InternalActionKeyId.deviceSwitchToProfile4 as VK])

/** 从配置中移除指定键的旧映射 */
function removeKeyFromConfig(kk: KK, isFn: boolean, config: DataProfile) {
  const index = config.Keys.findIndex((item) => item.Input.KK == kk && item.Input.WithFN == isFn)
  if (index > -1) config.Keys.splice(index, 1)
}

/** 写入新键位映射（KeyFire / 普通改键 / 默认重置的公共尾部） */
function applyKeyMapping(kk: KK, data: PostSetKey, config: DataProfile, mode: OutputMode, extension: number[]) {
  markKmeKeys(kk, MagnetSeniorType.SeniorKeyTypeNone, config, data.IsFn)
  removeKeyFromConfig(kk, data.IsFn, config)
  config.Keys.push({
    Input: { KK: kk, WithFN: data.IsFn, WithModifier: data.KeyModifier[kk] ?? [] },
    Output: { Mode: mode, VK: data.Key[kk], Extension: extension } as Output,
  })
}

/**
 * 尝试执行 FN 键出厂重置（仅 FN 层且设备支持时生效）
 * 返回 true 表示已由硬件处理，调用方应跳过后续的 applyKeyMapping
 */
async function tryFnFactoryReset(kk: KK, data: PostSetKey, config: DataProfile, currentKey: KIO | undefined): Promise<boolean> {
  const fnReset = data.IsFn ? deviceManager.deviceDriver.fnFactoryReset : undefined
  if (!fnReset) return false

  markKmeKeys(kk, MagnetSeniorType.SeniorKeyTypeNone, config, data.IsFn)
  if (currentKey?.Output.Mode == OutputMode.Macro) config.Macros.delete(currentKey.Output.DataIndex)
  if (currentKey?.Output.Mode == OutputMode.Magnet) config.MagnetData.delete(currentKey.Output.DataIndex)
  removeKeyFromConfig(kk, data.IsFn, config)
  await fnReset.resetToDefault(kk)
  return true
}

/**
 * 火柴人板载配置：固件会自动将切换键映射同步到所有硬件槽位，
 * 本地其他槽位存档也需同步更新，防止切换槽位后 syncKeys() 写入旧数据覆盖固件已完成的同步
 */
function propagateSwitchKeysToOtherProfiles(changeKeys: string[], data: PostSetKey) {
  const switchConfigChanges = changeKeys
    .filter((k) => onboardSwitchActionIds.has(data.Key[Number(k) as KK]))
    .map((k) => {
      const kk = Number(k) as KK
      return { kk, isFn: data.IsFn, output: { Mode: OutputMode.Key, VK: data.Key[kk], Extension: [] } as unknown as Output }
    })
  if (switchConfigChanges.length > 0) {
    dataProfileManage().propagateKeysToOtherOnboardProfiles(switchConfigChanges)
  }
}

export const handleSetkey = async (data: PostSetKey) => {
  const config = dataProfileManage().dataProfile
  const changeKeys = Object.keys(data.Key)
  let shouldSyncKeys = false
  let shouldSaveProfile = false

  for (const k of changeKeys) {
    const kk = Number(k) as KK
    const currentKey = config.Keys.find((item) => item.Input.KK == kk && item.Input.WithFN == data.IsFn)

    if (data.KeyType == SetKeyType.KeyFire) {
      applyKeyMapping(kk, data, config, OutputMode.Fire, data.KeyFire[kk])
      shouldSyncKeys = true
      shouldSaveProfile = true
      continue
    }

    if (data.Key[kk] == defaultVK) {
      const didFnReset = await tryFnFactoryReset(kk, data, config, currentKey)
      if (didFnReset) {
        shouldSaveProfile = true
        continue
      }
      // 无 FN 出厂重置支持：记录 ResetKey 后交由 syncKeys 同步
      dataProfileManage().ResetKeys.push(currentKey ?? ({ Input: { KK: kk, WithFN: data.IsFn, WithModifier: data.KeyModifier[kk] ?? [] }, Output: { Mode: OutputMode.Key, VK: defaultVK } } as KIO))
      shouldSyncKeys = true
    } else {
      shouldSyncKeys = true
    }

    applyKeyMapping(kk, data, config, OutputMode.Key, [])
    shouldSaveProfile = true
  }

  if (shouldSaveProfile) {
    dataProfileManage().save()
  }

  // 检测本次设置是否包含切换键(C1-C4)，通知前端询问用户是否同步
  // 火柴人设备支持板载配置时（固件 >= 1.18），固件会自动同步切换键到所有配置，无需前端手动触发同步
  const isHuochairenWithOnboard = HuoChaiRenDevices.includes(deviceManager.deviceID) && deviceManager.deviceDriver.supportsOnboardConfigs()
  if (isHuochairenWithOnboard && shouldSaveProfile) {
    propagateSwitchKeysToOtherProfiles(changeKeys, data)
  }

  const hasSwitchKey = shouldSaveProfile && !isHuochairenWithOnboard && deviceManager.deviceDriver.supportsOnboardConfigs() && changeKeys.some((k) => onboardSwitchActionIds.has(data.Key[Number(k) as KK]))

  if (shouldSyncKeys) {
    await deviceManager.deviceDriver.syncKeys()
  }

  return { hasSwitchKey }
}
